import React, { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Wand2, Copy, RotateCcw, Upload, Loader2, Sparkles, Check, Layers, Lock, Cpu, ShieldCheck } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Link } from "react-router-dom";
import NaturalnessScore from "@/components/NaturalnessScore";
import ScoreBoostTips from "@/components/ScoreBoostTips";
import RewriteReport from "@/components/RewriteReport";

const TONES = ["natural", "friendly", "professional", "witty", "confident", "empathetic"];
const AUDIENCES = ["general", "students", "marketers", "executives", "developers", "creators"];
const FORMALITY = ["casual", "neutral", "formal"];
const LENGTHS = [
  { key: "shorter", label: "Shorter" },
  { key: "keep", label: "Keep length" },
  { key: "longer", label: "Longer" },
];
const STRENGTH_MAP = ["light", "medium", "strong"];

export default function Humanizer({ compact = false, styleProfiles = [] }) {
  const { user, refresh } = useAuth();
  const [text, setText] = useState("");
  const [result, setResult] = useState("");
  const [naturalness, setNaturalness] = useState(null);
  const [naturalnessBefore, setNaturalnessBefore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("");
  const [copied, setCopied] = useState(false);
  const [tone, setTone] = useState("natural");
  const [audience, setAudience] = useState("general");
  const [formality, setFormality] = useState("neutral");
  const [strengthIdx, setStrengthIdx] = useState(1);
  const [length, setLength] = useState("keep");
  const [styleProfileId, setStyleProfileId] = useState("");
  const [longDoc, setLongDoc] = useState(false);
  const [model, setModel] = useState("sonnet");
  const [preserve, setPreserve] = useState(false);
  const [inputReadsHuman, setInputReadsHuman] = useState(false);
  const [auditReport, setAuditReport] = useState(null);
  const [resultId, setResultId] = useState(null);
  const [modelCatalog, setModelCatalog] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/models").then(({ data }) => {
      setModelCatalog(data.models);
      setModel(data.default || "sonnet");
    }).catch(() => {});
  }, [user?.plan]);

  const isPaid = user && (user.plan === "pro" || user.plan === "premium");
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;

  // Per-request limits, mirrored from the backend PLANS config
  const CHAR_LIMITS = { free: 2500, pro: 5000, premium: 20000 };
  const charLimit = user ? CHAR_LIMITS[user.plan] || CHAR_LIMITS.free : null;
  const anonWordLimit = 300;
  const overLimit = user
    ? text.length > charLimit && !(longDoc && isPaid)
    : words > anonWordLimit;
  const limitHint = user
    ? `${user.plan === "free" ? "Free plan" : `${user.plan[0].toUpperCase()}${user.plan.slice(1)} plan`}: ${charLimit.toLocaleString()} characters per request`
    : `Demo: ${anonWordLimit} words per request`;

  const onHumanize = async () => {
    if (!text.trim()) { toast.error("Paste some text first."); return; }
    if (overLimit) {
      toast.error(user
        ? `That's ${text.length.toLocaleString()} characters — ${charLimit.toLocaleString()} is the limit on your ${user.plan} plan.`
        : `That's ${words} words — the demo handles ${anonWordLimit}. Sign up free to send more.`);
      return;
    }
    setLoading(true); setResult(""); setCopied(false); setNaturalness(null); setNaturalnessBefore(null); setPhase(""); setInputReadsHuman(false); setAuditReport(null); setResultId(null);
    try {
      const payload = {
        text,
        tone, audience, formality,
        strength: STRENGTH_MAP[strengthIdx],
        length,
        style_profile_id: styleProfileId || null,
        chunk: longDoc && isPaid,
        model,
        preserve,
      };
      // Use streaming endpoint via fetch (EventSource can't POST)
      const token = localStorage.getItem("humanly_token");
      const res = await fetch(`${API}/humanize/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Request failed (${res.status})`);
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        // parse SSE events
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          let obj = null;
          try { obj = JSON.parse(data); } catch { continue; }
          if (obj.error) throw new Error(obj.error);
          if (obj.delta) { acc += obj.delta; setResult(acc); }
          if (obj.phase === "refining") {
            setPhase(obj.focus ? `Refining — ${obj.focus} (pass ${obj.pass} of ${obj.total})…`
                               : `Refining for stealth — pass ${obj.pass} of ${obj.total}…`);
          }
          if (obj.replace) { acc = obj.replace; setResult(acc); setPhase(""); }
          if (obj.done) {
            if (obj.id) setResultId(obj.id);
            if (obj.audit) setAuditReport(obj.audit);
            if (obj.input_reads_human) setInputReadsHuman(true);
            if (obj.naturalness) setNaturalness(obj.naturalness);
            if (obj.naturalness_before) setNaturalnessBefore(obj.naturalness_before);
            if (user) refresh();
          }
        }
      }
      toast.success("Humanized. Read it side-by-side below.");
    } catch (e) {
      toast.error(e?.message || "Something went wrong.");
    } finally { setLoading(false); setPhase(""); }
  };

  const onCopy = async () => {
    await navigator.clipboard.writeText(result);
    setCopied(true); setTimeout(() => setCopied(false), 1600);
    toast.success("Copied to clipboard");
    // keeping a rewrite is the signal that it sounded right — teach the voice profile
    if (resultId && styleProfileId && user) {
      api.post(`/history/${resultId}/accept`).then(({ data }) => {
        if (data?.fingerprint_rebuilt) toast.success("Your voice profile just got sharper.");
      }).catch(() => {});
    }
  };

  const onReset = () => {
    setText(""); setResult(""); setNaturalness(null); setNaturalnessBefore(null);
    setInputReadsHuman(false); setAuditReport(null); setResultId(null);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!user) { toast.error("Sign up to upload documents."); return; }
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/documents/extract", form, { headers: { "Content-Type": "multipart/form-data" } });
      setText(data.text);
      toast.success(`Loaded ${data.words} words from ${file.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { e.target.value = ""; }
  };

  return (
    <section data-testid="humanizer" className="surface p-5 md:p-8">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-6">
        {/* editor */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">Your text</div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <div className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="word-counter">{words} words</div>
              <span
                data-testid="limit-hint"
                className="text-[11px]"
                style={{ color: overLimit ? "hsl(15 62% 52%)" : "hsl(var(--muted-foreground))" }}
              >
                {overLimit ? `Over the limit — ${limitHint.toLowerCase()}` : limitHint}
              </span>
            </div>
          </div>
          <Textarea
            data-testid="humanize-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste your AI-generated text here — an email draft, blog paragraph, essay, product copy…"
            className="min-h-[220px] font-mono-editor text-sm bg-white resize-y border-[hsl(var(--border))] focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]/30 focus-visible:border-[hsl(var(--primary))]"
          />
          <div className="flex flex-wrap items-center gap-3">
            <button
              data-testid="humanize-submit-button"
              onClick={onHumanize}
              disabled={loading}
              className="btn-primary flex items-center gap-2 disabled:opacity-60 disabled:pointer-events-none"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
              {loading ? "Humanizing…" : "Humanize"}
            </button>
            <button data-testid="reset-button" onClick={onReset} className="btn-ghost flex items-center gap-2 text-sm">
              <RotateCcw size={14} /> Reset
            </button>
            <button
              data-testid="upload-button"
              onClick={() => fileRef.current?.click()}
              className="btn-ghost flex items-center gap-2 text-sm"
            >
              <Upload size={14} /> Upload PDF/DOCX/TXT
            </button>
            <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" onChange={onUpload} className="hidden" />
            {phase && (
              <span data-testid="refine-phase" className="text-xs text-[hsl(var(--accent))] flex items-center gap-1.5">
                <Loader2 size={12} className="animate-spin" /> {phase}
              </span>
            )}
          </div>

          {inputReadsHuman && !preserve && (
            <div data-testid="reads-human-warning" className="rounded-xl p-4 text-sm border border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/[0.07] fade-up">
              <div className="font-semibold mb-1">Your original already reads human</div>
              <p className="text-[hsl(var(--muted-foreground))]">
                Rewriting text you wrote yourself can make it <em>more</em> likely to be flagged — the rewrite carries the
                model's fingerprints, not yours. Turn on <strong>Preserve my voice</strong> in the controls to make only
                light corrections instead.
              </p>
            </div>
          )}

          {result && (
            <div className="mt-4 border-t border-[hsl(var(--border))] pt-6 fade-up">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="label-eyebrow mb-2">Original</div>
                  <div className="bg-[hsl(var(--muted))] rounded-xl p-4 text-sm leading-relaxed whitespace-pre-wrap max-h-[420px] overflow-auto">
                    {text}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="label-eyebrow flex items-center gap-2">
                      <Sparkles size={12} className="text-[hsl(var(--accent))]" /> Humanized
                    </div>
                    <button data-testid="copy-button" onClick={onCopy} className="pill text-xs">
                      {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                    </button>
                  </div>
                  <div data-testid="humanized-output" className="bg-white border border-[hsl(var(--border))] rounded-xl p-4 text-sm leading-relaxed whitespace-pre-wrap max-h-[420px] overflow-auto">
                    {result}
                  </div>
                </div>
              </div>
              {naturalness && <NaturalnessScore data={naturalness} before={naturalnessBefore} />}
              {auditReport && <RewriteReport audit={auditReport} />}
              {naturalness && !preserve && <ScoreBoostTips data={naturalness} isAuthed={!!user} />}
            </div>
          )}
        </div>

        {/* Controls */}
        <aside className="flex flex-col gap-5 border-l lg:pl-6 border-[hsl(var(--border))]">
          <div>
            <div className="label-eyebrow mb-2 flex items-center gap-1.5"><Cpu size={11} /> Model</div>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger data-testid="model-select" className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {modelCatalog.map((m) => (
                  <SelectItem
                    key={m.key}
                    value={m.key}
                    disabled={!m.unlocked}
                    data-testid={`model-option-${m.key}`}
                  >
                    <div className="flex items-center gap-2">
                      <span>{m.label}</span>
                      {!m.unlocked && <Lock size={11} className="text-[hsl(var(--muted-foreground))]" />}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {modelCatalog.find((m) => m.key === model)?.tagline && (
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1.5 leading-snug">
                {modelCatalog.find((m) => m.key === model).tagline}
              </div>
            )}
            {modelCatalog.some((m) => !m.unlocked) && (
              <Link to="/pricing" className="text-[11px] link-underline mt-1 inline-block text-[hsl(var(--accent))]">
                Unlock Signature →
              </Link>
            )}
          </div>
          <div>
            <div className="label-eyebrow mb-2">Tone</div>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger data-testid="tone-select" className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="label-eyebrow mb-2">Audience</div>
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger data-testid="audience-select" className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {AUDIENCES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="label-eyebrow mb-2">Formality</div>
            <Select value={formality} onValueChange={setFormality}>
              <SelectTrigger data-testid="formality-select" className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FORMALITY.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="label-eyebrow">Rewrite strength</div>
              <div className="text-xs font-mono-editor">{STRENGTH_MAP[strengthIdx]}</div>
            </div>
            <Slider
              data-testid="strength-slider"
              value={[strengthIdx]}
              onValueChange={(v) => setStrengthIdx(v[0])}
              min={0} max={2} step={1}
            />
          </div>
          <div>
            <div className="label-eyebrow mb-2">Length</div>
            <div className="flex gap-2">
              {LENGTHS.map((l) => (
                <button
                  key={l.key}
                  data-testid={`length-${l.key}`}
                  onClick={() => setLength(l.key)}
                  className={`flex-1 text-xs rounded-full py-2 border transition ${length === l.key ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "bg-white border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
          {user && (
            <div>
              <div className="label-eyebrow mb-2">Personal style</div>
              <Select value={styleProfileId || "none"} onValueChange={(v) => setStyleProfileId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="style-select" className="bg-white"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {styleProfiles.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Link to="/profile" className="text-xs link-underline mt-2 inline-block text-[hsl(var(--muted-foreground))]">
                Manage style profiles →
              </Link>
            </div>
          )}
          <div className="flex items-center justify-between gap-3 bg-[hsl(var(--secondary))] rounded-xl p-3">
            <div className="flex items-start gap-2">
              <ShieldCheck size={14} className="mt-0.5 text-[hsl(var(--primary))]" />
              <div>
                <div className="text-sm font-semibold">Preserve my voice</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">For writing that's already yours — light corrections only, no rewriting.</div>
              </div>
            </div>
            <Switch data-testid="preserve-toggle" checked={preserve} onCheckedChange={setPreserve} />
          </div>
          {isPaid && (
            <div className="flex items-center justify-between gap-3 bg-[hsl(var(--secondary))] rounded-xl p-3">
              <div className="flex items-start gap-2">
                <Layers size={14} className="mt-0.5 text-[hsl(var(--primary))]" />
                <div>
                  <div className="text-sm font-semibold">Long document mode</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">Auto-split & stitch inputs beyond your plan's per-request limit.</div>
                </div>
              </div>
              <Switch data-testid="long-doc-toggle" checked={longDoc} onCheckedChange={setLongDoc} />
            </div>
          )}
          {!user && !compact && (
            <div className="rounded-xl bg-[hsl(var(--secondary))] p-4 text-sm">
              <div className="font-semibold mb-1">Save your rewrites</div>
              <p className="text-[hsl(var(--muted-foreground))] mb-3">Create a free account for history, style profiles, and higher limits.</p>
              <Link to="/signup" data-testid="cta-signup-sidebar" className="btn-primary text-xs py-2 px-3 inline-block">Create free account</Link>
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}
